<template>
  <div class="transaction-page">
    <!-- Loading indicator -->
    <div v-if="transactionStore.loading" class="flex flex-column align-items-center py-5">
      <i class="pi pi-spin pi-spinner text-6xl text-primary mb-4"></i>
      <p>Loading transaction data...</p>
    </div>

    <div v-else>
      <!-- Header with action buttons -->
      <div class="flex align-items-center justify-content-between mb-4">
        <h2 class="text-2xl font-semibold m-0">Edit Transaction</h2>
        <div class="flex gap-2">
          <Button label="Cancel" icon="pi pi-times" class="p-button-outlined" @click="cancel" />
          <Button label="Save Transaction" icon="pi pi-check" @click="saveTransaction" />
        </div>
      </div>

      <!-- Person Selection -->
      <Card class="mb-4">
        <template #header>
          <div class="flex align-items-center justify-content-between">
            <div class="flex align-items-center">
              <i class="pi pi-user mr-2"></i>
              <h5 class="m-0">
                {{
                  transaction.transaction_type === 'purchase'
                    ? 'Supplier Contact'
                    : 'Customer Contact'
                }}
              </h5>
            </div>
          </div>
        </template>
        <template #content>
          <div class="grid">
            <div class="col-12 mb-3">
              <div class="flex gap-2">
                <span class="p-float-label flex-1">
                  <AutoComplete
                    v-model="selectedPerson"
                    :suggestions="filteredPeopleOptions"
                    @complete="searchPeople"
                    @item-select="onPersonSelect"
                    field="displayName"
                    dropdown
                    class="w-full"
                    optionLabel="displayName"
                  />
                  <label for="personSelect">
                    {{
                      transaction.transaction_type === 'purchase'
                        ? 'Supplier Contact'
                        : 'Customer Contact'
                    }}
                  </label>
                </span>
                <Button
                  icon="pi pi-qrcode"
                  class="p-button-rounded p-button-outlined"
                  @click="openIdScanner"
                  aria-label="Scan ID Barcode"
                  title="Scan ID Barcode"
                />
              </div>
            </div>

            <!-- Display selected person information -->
            <div
              v-if="selectedPersonId"
              class="col-12 mb-3 p-2 border-1 surface-border border-round"
            >
              <div class="flex flex-column gap-2">
                <div class="text-lg font-semibold">
                  {{ transactionModel?.party.name || 'Unknown' }}
                </div>
                <div
                  v-if="selectedPerson?.email || transaction.personSnapshot?.email"
                  class="flex align-items-center gap-2"
                >
                  <i class="pi pi-envelope text-sm"></i>
                  <span>{{ selectedPerson?.email || transaction.personSnapshot?.email }}</span>
                </div>
                <div
                  v-if="selectedPerson?.phone || transaction.personSnapshot?.phone"
                  class="flex align-items-center gap-2"
                >
                  <i class="pi pi-phone text-sm"></i>
                  <span>{{ selectedPerson?.phone || transaction.personSnapshot?.phone }}</span>
                </div>
                <div v-if="transactionModel?.party.address" class="flex align-items-center gap-2">
                  <i class="pi pi-map-marker text-sm"></i>
                  <span>{{ transactionModel?.party.address }}</span>
                </div>
                <div
                  v-if="selectedPerson?.idNumber || transaction.personSnapshot?.idNumber"
                  class="flex align-items-center gap-2"
                >
                  <i class="pi pi-id-card text-sm"></i>
                  <span
                    >ID:
                    {{ selectedPerson?.idNumber || transaction.personSnapshot?.idNumber }}</span
                  >
                </div>
              </div>
            </div>

            <div class="col-12 flex justify-content-between">
              <Button
                label="Create New Contact"
                icon="pi pi-plus"
                class="p-button-sm"
                @click="showCreatePersonDialog = true"
              />

              <!-- Edit person button -->
              <Button
                v-if="selectedPersonId"
                label="Edit Contact"
                icon="pi pi-pencil"
                class="p-button-sm p-button-outlined"
                @click="openEditPersonDialog"
              />
            </div>
          </div>
        </template>
      </Card>

      <!-- Organization Selection -->
      <Card class="mb-4">
        <template #header>
          <div class="flex align-items-center justify-content-between">
            <div class="flex align-items-center">
              <i class="pi pi-building mr-2"></i>
              <h5 class="m-0">
                {{
                  transaction.transaction_type === 'purchase'
                    ? 'Supplier Company'
                    : 'Customer Company'
                }}
              </h5>
            </div>
          </div>
        </template>
        <template #content>
          <div class="grid">
            <div class="col-12 mb-3">
              <span class="p-float-label">
                <AutoComplete
                  v-model="selectedOrganization"
                  :suggestions="filteredOrganizationOptions"
                  @complete="searchOrganizations"
                  @item-select="onOrganizationSelect"
                  field="name"
                  dropdown
                  class="w-full"
                  optionLabel="name"
                />
                <label for="organizationSelect">
                  {{
                    transaction.transaction_type === 'purchase'
                      ? 'Supplier Company'
                      : 'Customer Company'
                  }}
                </label>
              </span>
            </div>

            <!-- Display selected organization information -->
            <div
              v-if="selectedOrganizationId"
              class="col-12 mb-3 p-2 border-1 surface-border border-round"
            >
              <div class="flex flex-column gap-2">
                <div class="text-lg font-semibold">
                  {{
                    selectedOrganization?.name ||
                    transaction.organizationSnapshot?.name ||
                    'Unknown'
                  }}
                </div>
                <div
                  v-if="selectedOrganization?.email || transaction.organizationSnapshot?.email"
                  class="flex align-items-center gap-2"
                >
                  <i class="pi pi-envelope text-sm"></i>
                  <span>{{
                    selectedOrganization?.email || transaction.organizationSnapshot?.email
                  }}</span>
                </div>
                <div
                  v-if="selectedOrganization?.phone || transaction.organizationSnapshot?.phone"
                  class="flex align-items-center gap-2"
                >
                  <i class="pi pi-phone text-sm"></i>
                  <span>{{
                    selectedOrganization?.phone || transaction.organizationSnapshot?.phone
                  }}</span>
                </div>
                <div
                  v-if="
                    selectedOrganization?.address || transaction.organizationSnapshot?.streetAddress
                  "
                  class="flex align-items-center gap-2"
                >
                  <i class="pi pi-map-marker text-sm"></i>
                  <span>{{
                    selectedOrganization?.address ||
                    [
                      transaction.organizationSnapshot?.streetAddress,
                      transaction.organizationSnapshot?.city,
                      transaction.organizationSnapshot?.state,
                      transaction.organizationSnapshot?.postalCode,
                    ]
                      .filter(Boolean)
                      .join(', ')
                  }}</span>
                </div>
              </div>
            </div>

            <div class="col-12 flex justify-content-between">
              <Button
                label="Create New Company"
                icon="pi pi-plus"
                class="p-button-sm"
                @click="showCreateOrganizationDialog = true"
              />

              <!-- Edit organization button -->
              <Button
                v-if="selectedOrganizationId"
                label="Edit Company"
                icon="pi pi-pencil"
                class="p-button-sm p-button-outlined"
                @click="openEditOrganizationDialog"
              />
            </div>
          </div>
        </template>
      </Card>

      <TransactionVehicleCard :transaction-id="transactionId" class="mb-3" />

      <!-- Transaction Details -->
      <TransactionDetailsForm
        v-model:transactionDate="transaction.transaction_date"
        v-model:transactionType="transaction.transaction_type"
        v-model:paymentMethod="transaction.payment_method"
        v-model:paymentStatus="transaction.payment_status"
        v-model:notes="notesProxy"
      />

      <!-- Transaction Items -->
      <TransactionItemList
        :items="transaction.items || []"
        :commodity-types="commodityTypes"
        @add-item="addNewItem"
        @edit-item="editItem"
        @remove-item="removeItem"
        @bulk-update="handleBulkUpdate"
      />

      <TransactionItemFormDialog
        v-model:visible="showItemDialog"
        :commodity-types="commodityTypes"
        :item-index="editingItemIndex"
        @save="onItemSaved"
        @cancel="onItemCancelled"
      />

      <!-- Create Person Dialog -->
      <PersonDialog
        v-model:visible="showCreatePersonDialog"
        :person-data="newPerson"
        :is-editing="false"
        @person-saved="onPersonCreated"
        @cancel="showCreatePersonDialog = false"
      />

      <!-- Edit Person Dialog -->
      <PersonDialog
        v-model:visible="showEditPersonDialog"
        :person-data="editingPerson"
        :is-editing="true"
        @person-saved="onPersonUpdatedFromScan"
        @cancel="showEditPersonDialog = false"
      />

      <!-- Create Organization Dialog -->
      <OrganizationDialog
        v-model:visible="showCreateOrganizationDialog"
        :organization-data="newOrganization"
        :is-editing="false"
        @organization-saved="onOrganizationCreated"
        @cancel="showCreateOrganizationDialog = false"
      />

      <!-- Edit Organization Dialog -->
      <OrganizationDialog
        v-model:visible="showEditOrganizationDialog"
        :organization-data="editingOrganization"
        :is-editing="true"
        @organization-saved="onOrganizationUpdated"
        @cancel="showEditOrganizationDialog = false"
      />

      <Card class="mb-4">
        <template #header>
          <div class="flex align-items-center">
            <i class="pi pi-pencil mr-2"></i>
            <h5 class="m-0">
              {{ transaction.transaction_type === 'purchase' ? 'Supplier' : 'Customer' }} Signature
            </h5>
          </div>
        </template>
        <template #content>
          <div class="signature-preview">
            <div v-if="transaction.party_signature" class="signature-image">
              <img :src="transaction.party_signature.data_url" alt="Party Signature" />
              <small
                >Signed:
                {{
                  transaction.party_signature.timestamp &&
                  Object.keys(transaction.party_signature.timestamp).length > 0
                    ? transaction.party_signature.timestamp.toDateString()
                    : transactionStore.original?.party_signature?.timestamp
                      ? transactionStore.original.party_signature.timestamp.toDateString()
                      : 'Date unknown'
                }}
              </small>
            </div>
            <div v-else class="signature-placeholder">
              <i class="pi pi-user"></i>
              <span
                >No
                {{ transaction.transaction_type === 'purchase' ? 'supplier' : 'customer' }}
                signature</span
              >
            </div>
          </div>
          <Button
            :label="`Capture ${transaction.transaction_type === 'purchase' ? 'Supplier' : 'Customer'} Signature`"
            icon="pi pi-pencil"
            class="p-button-outlined mt-2 w-full"
            @click="showSignatureDialog = true"
          />
        </template>
      </Card>

      <SignatureDialog
        v-model="showSignatureDialog"
        title="Declaration of Ownership"
        :subtitle="transactionModel?.party.name || ''"
        :transaction-type="transaction.transaction_type"
        :show-legal-declaration="transaction.transaction_type === 'purchase'"
        @save="savePartySignature"
      />

      <!-- ID Scanner Dialog -->
      <IdScanner v-model:visible="showIdScanner" @scan="onScanSuccess" @error="onScanError" />

      <!-- Scan Results Dialog -->
      <ScanResultsDialog
        v-model:visible="showScanResults"
        :scannedData="scannedData!"
        :matchingPersons="matchingPersons"
        @select-person="onSelectPerson"
        @create-new-person="onCreateNewPerson"
        @cancel="onScanResultsCancel"
      />

      <!-- Transaction Summary -->
      <Card class="mb-4 border-green-200 surface-card">
        <template #header>
          <div class="flex align-items-center bg-green-50 pb-0">
            <i class="pi pi-calculator text-green-500 mr-2"></i>
            <h5 class="m-0 text-green-700">Transaction Summary</h5>
          </div>
        </template>
        <template #content>
          <div class="grid">
            <div class="col-12 md:col-6">
              <div class="text-lg mb-2">
                Total Items:
                <span class="font-semibold">{{ (transaction.items || []).length }}</span>
              </div>
              <div class="text-lg mb-2">
                Total Weight:
                <span class="font-semibold">{{ transactionModel?.totalWeight || 0 }} lbs</span>
              </div>
            </div>
            <div class="col-12 md:col-6 text-right">
              <div class="text-2xl text-green-600 font-bold">
                Total Amount:
                {{
                  TransactionModel.formatCurrency(parseFloat(transactionModel?.totalAmount || '0'))
                }}
              </div>
            </div>
          </div>
        </template>
      </Card>
    </div>
  </div>
</template>

<script setup lang="ts">
import { ref, computed, onMounted, watch, nextTick } from 'vue'
import { useRouter, useRoute } from 'vue-router'
import { doc, Timestamp } from 'firebase/firestore'
import { storage } from '@/firebase/config'
import { useCommodityTypeService } from '@/services/commodityTypeService'
import { useToast } from 'primevue/usetoast'
import TransactionVehicleCard from '@/components/transaction/TransactionVehicleCard.vue'
import IdScanner from '@/components/common/IdScanner.vue'
import ScanResultsDialog from '@/components/common/ScanResultsDialog.vue'
import type { ScannedIdData } from '@/composables/useIdScanner'

// Import components
import TransactionItemFormDialog from '@/components/transaction/TransactionItemFormDialog.vue'
import TransactionDetailsForm from '@/components/TransactionDetailsForm.vue'
import TransactionItemList from '@/components/TransactionItemList.vue'
import { createTransaction } from '@/factories/transactionFactory'
import PersonDialog from '@/components/person/PersonDialog.vue'
import OrganizationDialog from '@/components/organization/OrganizationDialog.vue'
import SignatureDialog from '@/components/SignatureDialog.vue'

// Import services and composables
import { usePersonStore } from '@/stores/v2/person'
import { useOrganizationStore } from '@/stores/v2/organization'
import { createPerson } from '@/factories/personFactory'
import { createOrganization } from '@/factories/organizationFactory'
import { useTransactionStore } from '@/stores/transactionStoreOld'
import { TransactionModel } from '@/models/TransactionModel'
import { TransactionPartyModel } from '@/models/TransactionPartyModel'
import type { Person } from '@/types/Person'
import type { Organization } from '@/types/Organization'

// PrimeVue components
import Card from 'primevue/card'
import Button from 'primevue/button'
import AutoComplete from 'primevue/autocomplete'
import Select from 'primevue/select'
import { useConfirm } from 'primevue/useconfirm'
import type { CommodityType } from '@/types/CommodityType'
import type { AutoCompleteCompleteEvent } from 'primevue/autocomplete'

const toast = useToast()
const router = useRouter()
const route = useRoute()
const transactionStore = useTransactionStore()
const personStore = usePersonStore()
const organizationStore = useOrganizationStore()
const commodityTypeService = useCommodityTypeService()
const confirm = useConfirm()

// Local UI state
const loading = ref(true)
const showCreatePersonDialog = ref(false)
const showEditPersonDialog = ref(false)
const showCreateOrganizationDialog = ref(false)
const showEditOrganizationDialog = ref(false)
const showSignatureDialog = ref(false)
const showItemDialog = ref(false)
const editingItemIndex = ref(-1)
type PersonOption = Person & { displayName?: string }

const commodityTypes = ref<CommodityType[]>([])
const selectedPerson = ref<PersonOption | null>(null)
const selectedOrganization = ref<Organization | null>(null)
const filteredPeopleOptions = ref<PersonOption[]>([])
const filteredOrganizationOptions = ref<Organization[]>([])
const editingPerson = ref<Partial<Person>>({})
const editingOrganization = ref<Partial<Organization>>({})
const selectedPersonId = ref<string>('')
const selectedOrganizationId = ref<string>('')

// Barcode scanner state
// ID Scanner state
const showIdScanner = ref(false)
const showScanResults = ref(false)
const scannedData = ref<ScannedIdData | null>(null)
const matchingPersons = ref<(Person & { matchReason: string })[]>([])
const pendingSelectPersonIdFromScan = ref<string | null>(null)

// Computed properties
const transactionId = computed(() => route.params.id as string)
const transaction = computed(() => transactionStore.draft || createTransaction())

const transactionModel = computed(() => {
  if (transaction.value) {
    return new TransactionModel(transaction.value)
  }
  return null
})

const isFormValid = computed(() => {
  return transactionModel.value?.isFormValid || false
})

const newPerson = ref(createPerson({ status: 'active' }))

// Initialize new organization data
const newOrganization = ref(
  createOrganization({
    type: transaction.value.transaction_type === 'purchase' ? 'supplier' : 'customer',
    status: 'active',
  }),
)

// v-model proxy for notes: TransactionDetailsForm expects string | undefined
const notesProxy = computed<string | undefined>({
  get() {
    return (transaction.value as any).notes ?? undefined
  },
  set(value) {
    if (transaction.value.id) {
      transactionStore.updateDraft({ notes: value ?? null })
    }
  },
})

// Methods
async function searchPeople(event: { query: string }) {
  const query = String(event.query || '').trim()
  try {
    const search = await personStore.search()
    // Basic name search; type filtering applied client-side if needed
    const [first, last] = query.split(' ')
    let results = await search.byName(first || undefined, last || undefined).run()
    const transactionType = transaction.value.transaction_type
    const personType = transactionType === 'purchase' ? 'supplier' : 'customer'
    results = results.filter((p) => !p.type || p.type === personType || p.type === 'both')
    filteredPeopleOptions.value = results.map((person: Person) => ({
      ...person,
      displayName: `${person.firstName}${person.middleName ? ' ' + person.middleName : ''} ${person.lastName}`,
    }))
  } catch (e) {
    console.error('People search failed:', e)
    filteredPeopleOptions.value = []
  }
}

async function searchOrganizations(event: { query: string }) {
  const query = String(event.query || '')
    .trim()
    .toLowerCase()
  const transactionType = transaction.value.transaction_type
  const orgType = transactionType === 'purchase' ? 'supplier' : 'customer'
  try {
    const search = await organizationStore.search()
    const results = await search.byType(orgType).byText(query).run()
    filteredOrganizationOptions.value = results
  } catch (e) {
    console.error('Organization search failed:', e)
    filteredOrganizationOptions.value = []
  }
}

async function onPersonSelect(event: { value: Person }) {
  if (!event.value.id) return
  selectedPersonId.value = event.value.id

  // Update the transaction in the store with the personSnapshot
  if (transaction.value.id) {
    const person = event.value
    await transactionStore.updateDraft({
      personId: person.id,
      personSnapshot: {
        firstName: person.firstName || '',
        middleName: person.middleName || '',
        lastName: person.lastName || '',
        email: person.email || '',
        phone: person.phone || '',
        streetAddress: person.streetAddress || '',
        city: person.city || '',
        state: person.state || '',
        postalCode: person.postalCode || '',
        country: person.country || '',
        idNumber: person.idNumber || '',
        photoIdUrl: person.photoIdUrl || '',
      },
    })
  }

  // If the person has an associated organization, select it
  if (event.value.organizationId) {
    const org = await organizationStore.get(event.value.organizationId as string)
    if (org) {
      selectedOrganization.value = org
      if (org.id) selectedOrganizationId.value = org.id
      await onOrganizationSelect({ value: org })
    }
  }
}

async function onOrganizationSelect(event: { value: Organization }) {
  if (!event.value.id) return
  selectedOrganizationId.value = event.value.id

  // Update the transaction in the store with the organizationSnapshot
  if (transaction.value.id) {
    const org = event.value
    await transactionStore.updateDraft({
      organizationId: org.id,
      organizationSnapshot: {
        name: org.name || '',
        phone: org.phone || '',
        email: org.email || '',
        streetAddress: org.address || '',
        city: org.city || '',
        state: org.state || '',
        postalCode: org.postalCode || '',
        country: org.country || '',
      },
    })
  }
}

async function onPersonCreated(createdPerson: Person) {
  // Add the new person to the store
  const newId = await personStore.create(createdPerson)
  const newPerson = (await personStore.get(newId)) as Person

  // Select the newly created person
  if (!newPerson.id) return
  selectedPersonId.value = newPerson.id
  selectedPerson.value = {
    ...newPerson,
    displayName: `${newPerson.firstName}${newPerson.middleName ? ' ' + newPerson.middleName : ''} ${newPerson.lastName}`,
  }

  // Update the transaction in the store
  if (transaction.value.id) {
    transactionStore.updateDraft({
      personId: newPerson.id,
      personSnapshot: {
        firstName: newPerson.firstName || '',
        middleName: newPerson.middleName || '',
        lastName: newPerson.lastName || '',
        email: newPerson.email || '',
        phone: newPerson.phone || '',
        streetAddress: newPerson.streetAddress || '',
        city: newPerson.city || '',
        state: newPerson.state || '',
        postalCode: newPerson.postalCode || '',
        country: newPerson.country || '',
        idNumber: newPerson.idNumber || '',
        photoIdUrl: newPerson.photoIdUrl || '',
      },
    })
  }

  toast.add({
    severity: 'success',
    summary: 'Success',
    detail: 'Contact created successfully',
    life: 3000,
  })

  // Close the dialog
  showCreatePersonDialog.value = false
}

async function onOrganizationCreated(createdOrg: Organization) {
  // Add the new organization to the store
  const newId = await organizationStore.create(createdOrg)
  const newOrg = (await organizationStore.get(newId)) as Organization

  // Select the newly created organization
  if (newOrg.id) selectedOrganizationId.value = newOrg.id
  selectedOrganization.value = newOrg

  // Update the transaction in the store
  if (transaction.value.id) {
    transactionStore.updateDraft({
      organizationId: newOrg.id,
      organizationSnapshot: {
        name: newOrg.name || '',
        phone: newOrg.phone || '',
        email: newOrg.email || '',
        streetAddress: newOrg.address || '',
        city: newOrg.city || '',
        state: newOrg.state || '',
        postalCode: newOrg.postalCode || '',
        country: newOrg.country || '',
      },
    })
  }

  toast.add({
    severity: 'success',
    summary: 'Success',
    detail: 'Company created successfully',
    life: 3000,
  })

  // Close the dialog
  showCreateOrganizationDialog.value = false
}

async function onPersonUpdated(updatedPerson: Person) {
  if (!updatedPerson.id) return
  // Update the person in the store
  personStore.update(updatedPerson.id, updatedPerson)

  // Update the transaction in the store if this is the selected person
  if (selectedPersonId.value === updatedPerson.id && transaction.value.id) {
    transactionStore.updateDraft({
      personSnapshot: {
        firstName: updatedPerson.firstName || '',
        middleName: updatedPerson.middleName || '',
        lastName: updatedPerson.lastName || '',
        email: updatedPerson.email || '',
        phone: updatedPerson.phone || '',
        streetAddress: updatedPerson.streetAddress || '',
        city: updatedPerson.city || '',
        state: updatedPerson.state || '',
        postalCode: updatedPerson.postalCode || '',
        country: updatedPerson.country || '',
        idNumber: updatedPerson.idNumber || '',
        photoIdUrl: updatedPerson.photoIdUrl || '',
      },
    })
  }

  // If the person has an updated organization, select it
  if (
    updatedPerson.organizationId &&
    updatedPerson.organizationId !== selectedOrganizationId.value
  ) {
    const org = await organizationStore.get(updatedPerson.organizationId as string)
    if (org) {
      selectedOrganization.value = org
      if (org.id) selectedOrganizationId.value = org.id
      onOrganizationSelect({ value: org })
    }
  }

  toast.add({
    severity: 'success',
    summary: 'Success',
    detail: 'Contact updated successfully',
    life: 3000,
  })

  // Close the dialog
  showEditPersonDialog.value = false
}

// If edit was opened due to a name-only match, update selection after save
function onPersonUpdatedFromScan(updatedPerson: Person) {
  onPersonUpdated(updatedPerson)
  if (
    pendingSelectPersonIdFromScan.value &&
    updatedPerson.id === pendingSelectPersonIdFromScan.value
  ) {
    // Re-run selection so person snapshot reflects any new idNumber/barcodeHash
    onSelectPerson(updatedPerson)
    pendingSelectPersonIdFromScan.value = null
  }
}

function onOrganizationUpdated(updatedOrg: Organization) {
  // Update the organization in the store
  if (!updatedOrg.id) return
  organizationStore.update(updatedOrg.id, updatedOrg)

  // Update the transaction in the store if this is the selected organization
  if (selectedOrganizationId.value === updatedOrg.id && transaction.value.id) {
    transactionStore.updateDraft({
      organizationSnapshot: {
        name: updatedOrg.name || '',
        phone: updatedOrg.phone || '',
        email: updatedOrg.email || '',
        streetAddress: updatedOrg.address || '',
        city: updatedOrg.city || '',
        state: updatedOrg.state || '',
        postalCode: updatedOrg.postalCode || '',
        country: updatedOrg.country || '',
      },
    })
  }

  toast.add({
    severity: 'success',
    summary: 'Success',
    detail: 'Company updated successfully',
    life: 3000,
  })

  // Close the dialog
  showEditOrganizationDialog.value = false
}

async function openEditPersonDialog() {
  // Fetch the selected person
  const person = await personStore.get(selectedPersonId.value)

  if (person) {
    // Create a copy of the person for editing
    editingPerson.value = { ...person } as any
    showEditPersonDialog.value = true
  } else {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Could not find contact details',
      life: 3000,
    })
  }
}

async function openEditOrganizationDialog() {
  // Fetch the selected organization
  const org = await organizationStore.get(selectedOrganizationId.value)

  if (org) {
    // Create a copy of the organization for editing
    editingOrganization.value = { ...org } as any
    showEditOrganizationDialog.value = true
  } else {
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Could not find company details',
      life: 3000,
    })
  }
}

async function cancel() {
  if (transactionStore.hasUnsavedChanges) {
    // Show confirmation dialog before discarding changes
    confirm.require({
      message: 'You have unsaved changes. Are you sure you want to discard them?',
      header: 'Discard Changes',
      icon: 'pi pi-exclamation-triangle',
      acceptClass: 'p-button-danger',
      accept: async () => {
        await transactionStore.cancelEditing()
        router.push('/transaction')
      },
      reject: () => {
        // User cancelled, stay on the page
      },
    })
  } else {
    await transactionStore.cancelEditing()
    router.push('/transaction')
  }
}

function addNewItem() {
  transactionStore.initNewItem()
  editingItemIndex.value = -1
  showItemDialog.value = true
}

function editItem(index: number) {
  transactionStore.editItem(index)
  editingItemIndex.value = index
  showItemDialog.value = true
}

function removeItem(index: number) {
  transactionStore.removeItem(index)
}

function onItemSaved() {
  if (transactionModel.value) {
    transactionStore.updateDraft({
      total_amount: transactionModel.value.totalAmountNumber,
    })
  }
  // Any additional logic after saving
  showItemDialog.value = false
}

function onItemCancelled() {
  // Any additional logic after cancelling
  showItemDialog.value = false
}

function handleBulkUpdate(updates: Array<{ index: number; updates: any }>) {
  try {
    // Apply bulk updates to the transaction items
    const updatedItems = [...(transaction.value.items || [])]

    updates.forEach(({ index, updates: itemUpdates }) => {
      if (index >= 0 && index < updatedItems.length) {
        // Apply the updates to the specific item
        updatedItems[index] = {
          ...updatedItems[index],
          ...itemUpdates,
        }

        // If weight was updated, recalculate total_price
        if (itemUpdates.weight || itemUpdates.price_per_lb) {
          const item = updatedItems[index]
          const price = Number(item.price_per_lb) || 0
          const netWeight = Number(item.weight?.net) || 0
          updatedItems[index].total_price = parseFloat((price * netWeight).toFixed(2))
        }
      }
    })

    // Update the transaction in the store
    transactionStore.updateDraft({
      items: updatedItems,
    })

    // Recalculate total amount
    if (transactionModel.value) {
      transactionStore.updateDraft({
        total_amount: transactionModel.value.totalAmountNumber,
      })
    }

    toast.add({
      severity: 'success',
      summary: 'Bulk Update Complete',
      detail: `Updated ${updates.length} item(s) successfully`,
      life: 3000,
    })
  } catch (error) {
    console.error('Error handling bulk update:', error)
    toast.add({
      severity: 'error',
      summary: 'Bulk Update Error',
      detail: 'Failed to update items. Please try again.',
      life: 5000,
    })
  }
}

function savePartySignature(signatureData: { dataUrl: string }) {
  // Update the transaction in the store
  if (transaction.value.id) {
    transactionStore.updateDraft({
      party_signature: {
        data_url: signatureData.dataUrl,
        timestamp: new Date(),
      },
    })
  }
}

async function saveTransaction() {
  try {
    if (!selectedPersonId.value && !selectedOrganizationId.value) {
      toast.add({
        severity: 'warn',
        summary: 'Missing Information',
        detail: 'Please select or create a contact or company for this transaction',
        life: 3000,
      })
      return
    }

    if (transaction.value.id) {
      await transactionStore.saveChanges()
    }

    toast.add({
      severity: 'success',
      summary: 'Transaction Saved',
      detail: 'Transaction has been successfully saved',
      life: 3000,
    })

    // Navigate to the transaction detail page
    router.push(`/transaction/${transaction.value.id}`)
  } catch (error) {
    console.error('Error saving transaction:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail: 'Failed to save transaction. Please try again.',
      life: 5000,
    })
  }
}

// ID Scanner functions
function openIdScanner() {
  showIdScanner.value = true
}

function onScanSuccess(data: ScannedIdData) {
  console.log('Scan successful:', data)

  // Save scanned data and find matches
  scannedData.value = data
  findMatchingPersons(data)

  // Prefer exact barcode hash match, then ID number match
  const hashMatch = matchingPersons.value.find(
    (p) => p.barcodeHash && p.barcodeHash === data.barcodeHash,
  )
  const idMatch = matchingPersons.value.find((p) => p.idNumber && p.idNumber === data.idNumber)

  const match = hashMatch || idMatch
  if (match) {
    // Auto-select the matching person
    onSelectPerson(match)
    toast.add({
      severity: 'success',
      summary: 'Existing Contact Selected',
      detail: `Matched ${match.firstName} ${match.lastName}`,
      life: 3000,
    })
  } else {
    // Show results dialog to create/select
    showScanResults.value = true
  }
}

function onScanError(error: string) {
  console.error('Scan error:', error)
  toast.add({
    severity: 'error',
    summary: 'Scan Error',
    detail: error,
    life: 5000,
  })
}

function findMatchingPersons(data: ScannedIdData) {
  ;(async () => {
    try {
      const search = await personStore.search()
      let results: Person[] = []
      if (data.barcodeHash) {
        results = await search.by2dBarcode(data.barcodeHash).run()
      } else if (data.idNumber) {
        results = await search.byIdNumber(data.idNumber).run()
      } else {
        results = await search.byExactName(data.firstName || '', data.lastName || '').run()
      }
      const annotated = results.map((p) => {
        let matchReason = 'Match'
        if (data.barcodeHash && p.barcodeHash === data.barcodeHash)
          matchReason = 'Barcode Hash Match'
        else if (data.idNumber && p.idNumber === data.idNumber) matchReason = 'ID Number Match'
        else if (
          p.firstName?.toLowerCase() === data.firstName.toLowerCase() &&
          p.lastName?.toLowerCase() === data.lastName.toLowerCase()
        ) {
          matchReason = 'Name Match'
        }
        return { ...(p as any), matchReason }
      })
      matchingPersons.value = annotated
      console.log(`Found ${annotated.length} matching persons:`, annotated)
    } catch (e) {
      console.error('Failed to search matching persons:', e)
      matchingPersons.value = []
    }
  })()
}

async function onSelectPerson(person: Person) {
  console.log('Selected person from scan results:', person)

  const scanned = scannedData.value
  const isHashMatch = !!(
    scanned &&
    person.barcodeHash &&
    person.barcodeHash === scanned.barcodeHash
  )
  const isIdMatch = !!(scanned && person.idNumber && person.idNumber === scanned.idNumber)

  if (scanned && !isHashMatch && !isIdMatch) {
    // Likely a name match only: open edit dialog pre-filled with scanned ID data
    editingPerson.value = {
      ...person,
      // Replace typical scan-mapped fields with newly scanned values
      firstName: scanned.firstName || person.firstName,
      lastName: scanned.lastName || person.lastName,
      middleName: scanned.middleName || person.middleName,
      idNumber: scanned.idNumber || person.idNumber,
      streetAddress: scanned.address || person.streetAddress,
      city: scanned.city || person.city,
      state: scanned.state || person.state,
      postalCode: scanned.postalCode || person.postalCode,
      country: scanned.country || person.country,
      barcodeHash: scanned.barcodeHash || person.barcodeHash,
      // Keep existing optional fields (email/phone/etc.) as-is
      type: transaction.value.transaction_type === 'purchase' ? 'supplier' : 'customer',
    }
    pendingSelectPersonIdFromScan.value = person.id ?? null
    showScanResults.value = false
    showEditPersonDialog.value = true
    return
  }

  // Exact match path: select immediately
  selectedPerson.value = {
    ...person,
    displayName: `${person.firstName}${person.middleName ? ' ' + person.middleName : ''} ${person.lastName}`,
  } as any
  if (person.id) selectedPersonId.value = person.id

  if (transaction.value.id) {
    await transactionStore.updateDraft({
      personId: person.id,
      personSnapshot: {
        firstName: person.firstName || '',
        middleName: person.middleName || '',
        lastName: person.lastName || '',
        email: person.email || '',
        phone: person.phone || '',
        streetAddress: person.streetAddress || '',
        city: person.city || '',
        state: person.state || '',
        postalCode: person.postalCode || '',
        country: person.country || '',
        idNumber: person.idNumber || '',
        photoIdUrl: person.photoIdUrl || '',
      },
    })
  }

  if (person.organizationId) {
    const org = await organizationStore.get(person.organizationId as string)
    if (org) {
      selectedOrganization.value = org as any
      if (org.id) selectedOrganizationId.value = org.id
      if (transaction.value.id) {
        await transactionStore.updateDraft({
          organizationId: org.id,
          organizationSnapshot: {
            name: org.name || '',
            phone: org.phone || '',
            email: org.email || '',
            streetAddress: org.address || '',
            city: org.city || '',
            state: org.state || '',
            postalCode: org.postalCode || '',
            country: org.country || '',
          },
        })
      }
    }
  }

  showScanResults.value = false
  toast.add({
    severity: 'success',
    summary: 'Person Selected',
    detail: `Selected ${person.firstName} ${person.lastName}`,
    life: 3000,
  })
}

function onCreateNewPerson(data: ScannedIdData) {
  console.log('Prefilling new person with scanned data:', data)
  // Prefill the create person dialog instead of immediately creating
  newPerson.value = createPerson({
    firstName: data.firstName,
    lastName: data.lastName,
    middleName: (data as any).middleName || '',
    idNumber: data.idNumber,
    streetAddress: data.address || '',
    city: data.city || '',
    state: data.state || '',
    postalCode: data.postalCode || '',
    country: data.country || '',
    dateOfBirth: data.dateOfBirth || '',
    barcodeHash: data.barcodeHash,
    type: transaction.value.transaction_type === 'purchase' ? 'supplier' : 'customer',
    status: 'active',
  })
  showScanResults.value = false
  showCreatePersonDialog.value = true
}

function onScanResultsCancel() {
  showScanResults.value = false
}

// Watch for changes in transaction type to update filtering
watch(
  () => transaction.value.transaction_type,
  async (newType) => {
    // Update the person type in the new person form
    newPerson.value.type = newType === 'purchase' ? 'supplier' : 'customer'

    // Update the organization type in the new organization form
    newOrganization.value.type = newType === 'purchase' ? 'supplier' : 'customer'
  },
)

// Load data on component mount
onMounted(async () => {
  try {
    // Load transaction data
    await transactionStore.startEditing(transactionId.value)
    console.log('Transaction loaded for editing:', transactionStore.draft)

    if (!transactionStore.draft) {
      toast.add({
        severity: 'error',
        summary: 'Error',
        detail: 'Could not load transaction for editing',
        life: 3000,
      })
      router.push('/transaction')
      return
    }

    // Fetch commodity types
    commodityTypes.value = await commodityTypeService.getActiveCommodityTypes()

    // v2 stores: fetch needed entities directly instead of bulk loading

    // Set local state from the loaded transaction
    if (transactionStore.draft.personId) {
      selectedPersonId.value = transactionStore.draft.personId

      // Fetch the person by id
      const person = await personStore.get(selectedPersonId.value)
      if (person) {
        selectedPerson.value = {
          ...person,
          displayName: `${person.firstName}${person.middleName ? ' ' + person.middleName : ''} ${person.lastName}`,
        }
      }
    }

    if (transactionStore.draft.organizationId) {
      selectedOrganizationId.value = transactionStore.draft.organizationId

      // Fetch the organization by id
      const org = await organizationStore.get(selectedOrganizationId.value)
      if (org) {
        selectedOrganization.value = org
      }
    }

    loading.value = false
  } catch (error) {
    console.error('Error loading data:', error)
    toast.add({
      severity: 'error',
      summary: 'Error',
      detail:
        'Failed to load necessary data: ' +
        (error instanceof Error ? error.message : String(error)),
      life: 5000,
    })
    loading.value = false
  }
})
</script>

<style scoped>
.signature-preview {
  border: 1px solid #ddd;
  border-radius: 4px;
  height: 120px; /* Reduced height for better aspect ratio */
  display: flex;
  align-items: center;
  justify-content: center;
  background-color: #f8f9fa;
  overflow: hidden;
  width: 100%;
}

.signature-image {
  width: 100%;
  height: 100%;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  padding: 10px;
}

.signature-image img {
  max-width: 100%;
  max-height: 80%;
  object-fit: contain;
  /* Preserve aspect ratio */
  width: auto;
  height: auto;
}

.signature-placeholder {
  display: flex;
  flex-direction: column;
  align-items: center;
  color: #aaa;
}

.signature-placeholder i {
  font-size: 2rem;
  margin-bottom: 0.5rem;
}

/* Barcode Scanner Styles */
.barcode-scanner-container {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 1rem;
}

.video-container {
  position: relative;
  width: 100%;
  max-width: 500px;
  height: 300px;
  border: 2px solid #ddd;
  border-radius: 8px;
  overflow: hidden;
}

.scanner-video {
  width: 100%;
  height: 100%;
  object-fit: cover;
}

.scanner-overlay {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(0, 0, 0, 0.3);
}

.scanner-target {
  width: 200px;
  height: 120px;
  border: 2px solid #fff;
  border-radius: 8px;
  position: relative;
  background: rgba(255, 255, 255, 0.1);
}

.scan-line {
  position: absolute;
  top: 0;
  left: 0;
  right: 0;
  height: 2px;
  background: #00ff00;
  animation: scan 2s linear infinite;
}

@keyframes scan {
  0% {
    top: 0;
  }
  50% {
    top: calc(100% - 2px);
  }
  100% {
    top: 0;
  }
}

.scanner-status {
  text-align: center;
  font-weight: 500;
  color: #666;
}

.scanner-tips {
  background: #f8f9fa;
  padding: 1rem;
  border-radius: 8px;
  border: 1px solid #e9ecef;
}

.scanner-tips ul {
  margin: 0;
  padding-left: 1.5rem;
}
</style>
